/**
 * CRM refund row drill-down.
 *
 * Renders from the row already in hand, so opening it costs no request. Its job
 * is to put the CRM decision and the accounts settlement side by side — the two
 * halves of a refund that live in different tables and, when accounts work from
 * the Django HTML screens, never reference each other.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { refundImageUrl } from '@/api/bi/refunds';
import type { CrmRefund } from '@/api/bi/crm';
import { REFUND_DECISION_TONE } from '@/lib/crmStatus';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const SETTLEMENT_TONE = {
  completed: 'success',
  pending: 'warning',
  cancelled: 'neutral',
} as const;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xs">{children}</div>
    </div>
  );
}

function SettlementScreenshot({ path }: { path: string }) {
  const [failed, setFailed] = useState(false);
  const url = refundImageUrl(path);

  if (!url || failed) {
    return (
      <div className="flex h-32 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        <ImageOff className="mr-1.5 h-4 w-4" />
        Screenshot could not be loaded
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img
        src={url}
        alt="Refund settlement screenshot"
        onError={() => setFailed(true)}
        className="h-32 w-full rounded-md border border-border object-cover"
      />
    </a>
  );
}

function Body({ refund }: { refund: CrmRefund }) {
  const s = refund.settlement;
  const discrepant = s?.status === 'completed' && refund.decision !== 'approved';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-border px-5 py-4">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          CRM refund request
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Request #{refund.id}</h2>
          <Badge tone={REFUND_DECISION_TONE[refund.decision]}>{refund.decision}</Badge>
          {s ? <Badge tone={SETTLEMENT_TONE[s.status]}>settlement {s.status}</Badge> : null}
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          <Link to={`/crm/tickets/${refund.ticket_id}`} className="hover:underline">
            Ticket #{refund.ticket_id}
          </Link>
          {refund.order_id != null ? (
            <>
              {' · '}
              <Link to={`/orders/${refund.order_id}`} className="hover:underline">
                Order #{refund.order_id}
              </Link>
            </>
          ) : null}
          {refund.customer?.name ? ` · ${refund.customer.name}` : ''}
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {discrepant ? (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-[11px] text-warning-soft">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Accounts have completed this settlement, but the CRM decision is{' '}
              <strong>{refund.decision}</strong>. Record the approval here so the
              ticket can close and the amount stops counting as unsettled leakage.
            </span>
          </div>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="space-y-3">
            <h3 className="text-[10px] uppercase tracking-wide text-muted-foreground">
              CRM decision
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Requested">{fmt.npr(refund.requested_amount)}</Field>
              <Field label="Approved">
                {refund.approved_amount != null ? fmt.npr(refund.approved_amount) : '—'}
              </Field>
              <Field label="Decided by">{refund.decided_by?.name ?? '—'}</Field>
              <Field label="Decided at">{fmt.datetime(refund.decided_at)}</Field>
              <Field label="Payment at order">
                {refund.payment_type_at_order || '—'}
              </Field>
              <Field label="Raised">{fmt.datetime(refund.created_at)}</Field>
            </div>
            {refund.customer_reason ? (
              <Field label="Customer reason">
                <p className="whitespace-pre-wrap text-muted-foreground">
                  {refund.customer_reason}
                </p>
              </Field>
            ) : null}
            {refund.internal_notes ? (
              <Field label="Internal notes">
                <p className="whitespace-pre-wrap text-muted-foreground">
                  {refund.internal_notes}
                </p>
              </Field>
            ) : null}
          </section>

          <section className="space-y-3">
            <h3 className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Accounts settlement
            </h3>
            {!s ? (
              <p className="text-xs text-muted-foreground">
                No <code className="text-[10px]">payment.Refund</code> record exists
                for this order yet, so accounts have not started the payout.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount">{fmt.npr(s.refund_amount)}</Field>
                  <Field label="Type">{s.refund_type}</Field>
                  <Field label="Created">{fmt.datetime(s.created_at)}</Field>
                  <Field label="Completed">{fmt.datetime(s.completed_at)}</Field>
                  <Field label="Settled by">
                    {s.settled_by?.name ?? (
                      <span className="text-muted-foreground">
                        Not recorded — the accounts HTML page does not capture it
                      </span>
                    )}
                  </Field>
                  <Field label="Source">
                    {refund.settlement_source === 'matched_by_order'
                      ? 'Matched by order id'
                      : 'Linked to this request'}
                  </Field>
                </div>
                {s.remarks ? (
                  <Field label="Remarks">
                    <p className="whitespace-pre-wrap text-muted-foreground">
                      {s.remarks}
                    </p>
                  </Field>
                ) : null}
                {s.image ? (
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                      Payout screenshot
                    </div>
                    <SettlementScreenshot path={s.image} />
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>

        <section className="mt-5">
          <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            Refund lines ({refund.items.length})
          </h3>
          {refund.items.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              No line items — this is a whole-order refund.
            </p>
          ) : (
            <div className="max-h-56 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/60 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Product</th>
                    <th className="px-3 py-1.5 text-right">Qty</th>
                    <th className="px-3 py-1.5 text-right">Unit price</th>
                    <th className="px-3 py-1.5 text-right">Line</th>
                    <th className="px-3 py-1.5 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {refund.items.map((it) => (
                    <tr key={it.id} className="border-t border-border">
                      <td className="px-3 py-1.5">{it.product_name ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right">
                        {it.quantity}
                        {it.quantity_in_order != null ? (
                          <span className="text-muted-foreground">
                            {' '}
                            / {it.quantity_in_order}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {fmt.npr(it.unit_price_snapshot)}
                      </td>
                      <td className="px-3 py-1.5 text-right">{fmt.npr(it.line_amount)}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {it.reason_code?.label || it.note || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function CrmRefundDetailDialog({
  refund,
  onClose,
}: {
  refund: CrmRefund | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={refund != null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[min(90vh,880px)] w-[calc(100vw-1.5rem)] max-w-4xl flex-col overflow-hidden p-0',
          'gap-0 sm:max-w-4xl',
        )}
      >
        <DialogTitle className="sr-only">Refund detail</DialogTitle>
        {refund ? <Body key={refund.id} refund={refund} /> : null}
      </DialogContent>
    </Dialog>
  );
}
